/**
 * Branch Web SDK - Cart Tracking
 * Tracks ADD_TO_CART events across the entire store
 *
 * Dependencies: branch-utils.js (must be loaded first)
 */

(function() {
  'use strict';

  // Check if utilities are loaded
  if (!window.BranchUtils) {
    console.error('BranchUtils not found. Load branch-utils.js first!');
    return;
  }

  // Track last added item to prevent duplicates
  let lastAddedItem = null;
  let lastAddTime = 0;

  /**
   * Track ADD_TO_CART event
   * @param {Object} item - Cart item data from Shopify
   */
  function trackAddToCart(item) {
    if (!item || !item.id) {
      return;
    }

    // Prevent duplicate tracking (same item within 2 seconds)
    const now = Date.now();
    const itemKey = `${item.product_id}_${item.variant_id || item.id}`;

    if (lastAddedItem === itemKey && (now - lastAddTime) < 2000) {
      if (window.BranchConfig.debug) {
        console.log('⚠️ Duplicate ADD_TO_CART prevented:', itemKey);
      }
      return;
    }

    lastAddedItem = itemKey;
    lastAddTime = now;

    // Wait for Branch SDK to be ready
    window.BranchUtils.whenBranchReady(function() {
      const eventData = {
        transaction_id: String(item.variant_id || item.id),
        revenue: window.BranchUtils.formatPrice(item.price || 0),
        currency: window.BranchConfig.currency,
        shipping: 0,
        tax: 0,
        affiliation: 'Shopify Store'
      };

      // Add commerce item data
      eventData.content_items = [window.BranchUtils.createCommerceItem(item)];

      // Track the event
      window.BranchUtils.trackEvent('ADD_TO_CART', eventData);
    });
  }

  // ============================================
  // Method 1: Listen to Shopify's pubsub events
  // ============================================
  if (window.pubsub && typeof window.pubsub.subscribe === 'function') {
    window.pubsub.subscribe('cart:item-added', function(event) {
      if (window.BranchConfig.debug) {
        console.log('🛒 Pubsub cart:item-added event:', event);
      }

      if (event && event.items && event.items.length > 0) {
        // Track the newly added item (last item in array)
        const addedItem = event.items[event.items.length - 1];
        trackAddToCart(addedItem);
      }
    });
  }

  // ============================================
  // Method 2: Listen to custom cart:updated event
  // ============================================
  document.addEventListener('cart:updated', function(event) {
    if (window.BranchConfig.debug) {
      console.log('🛒 Custom cart:updated event:', event);
    }

    if (event.detail && event.detail.item) {
      trackAddToCart(event.detail.item);
    }
  });

  // ============================================
  // Method 3: Intercept fetch to /cart/add.js
  // ============================================
  const originalFetch = window.fetch;

  window.fetch = function(...args) {
    const url = args[0];

    // Only intercept cart add requests
    if (typeof url === 'string' && url.includes('/cart/add')) {
      return originalFetch.apply(this, args).then(response => {
        // Only process successful responses
        if (response.ok) {
          // Clone response to avoid consuming it
          response.clone().json().then(data => {
            if (window.BranchConfig.debug) {
              console.log('🛒 Cart add fetch response:', data);
            }

            if (data.items && Array.isArray(data.items)) {
              // Multiple items added
              data.items.forEach(item => trackAddToCart(item));
            } else if (data.id) {
              // Single item added
              trackAddToCart(data);
            }
          }).catch(() => {
            // Silent fail if response is not JSON
          });
        }
        return response;
      });
    }

    // Not a cart add request, pass through
    return originalFetch.apply(this, args);
  };

  // ============================================
  // Method 4: Listen to cart/change.js (quantity updates)
  // ============================================
  const originalXHROpen = XMLHttpRequest.prototype.open;
  const originalXHRSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function(method, url) {
    this._url = url;
    this._method = method;
    return originalXHROpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function() {
    if (this._url && this._url.includes('/cart/add')) {
      this.addEventListener('load', function() {
        if (this.status === 200) {
          try {
            const data = JSON.parse(this.responseText);
            if (window.BranchConfig.debug) {
              console.log('🛒 Cart add XHR response:', data);
            }

            if (data.items && Array.isArray(data.items)) {
              data.items.forEach(item => trackAddToCart(item));
            } else if (data.id) {
              trackAddToCart(data);
            }
          } catch (e) {
            // Not JSON or parsing failed
          }
        }
      });
    }
    return originalXHRSend.apply(this, arguments);
  };

  // Debug log
  if (window.BranchConfig.debug) {
    console.log('🛒 Branch Cart Tracking initialized');
  }

})();
